/** Compact native photo fields without replacing Teeinblue upload handlers. */
(() => {
  'use strict';
  if (window.TeePhotoStrip) return;
  const roots = new Map();
  const lifecycle = new AbortController();
  let helpSequence = 0;
  const CONTRACT = Object.freeze({
    photoFields: '#teeFormInputs > .tee-field--photo',
    fileInput: 'input[type="file"]',
    artwork: 'svg.tee-mockup-artwork',
    photoLayer: '.tee-layer-photo',
    fileIdPrefix: 'tee-photo-',
    artworkLayerIdPrefix: 'artwork-layer--',
    fieldError: '.tee-required-message',
    uploadMessage: '.tee-photo-message'
  });
  const fieldSelector = CONTRACT.photoFields;

  /** Pending fields stay hidden here. Revealing them to measure reflows the form, and scroll anchoring then moves the page. */
  function isFieldShown(field) {
    if (!field.classList.contains('tee-photo-pending')) return field.getClientRects().length > 0;
    return !field.hidden && field.style.display !== 'none' && field.parentElement?.getClientRects().length > 0;
  }

  /** Resolve field order from SVG paint order, never from labels or numeric IDs. */
  function resolveArtworkOrder(root, fields) {
    if (!fields.length) return null;
    const keys = fields.map(field => {
      const id = field.querySelector(CONTRACT.fileInput)?.id || '';
      return id.startsWith(CONTRACT.fileIdPrefix)
        ? CONTRACT.artworkLayerIdPrefix + id.slice(CONTRACT.fileIdPrefix.length)
        : null;
    });
    if (keys.some(key => !key) || new Set(keys).size !== keys.length) return null;

    const candidates = [...root.ownerDocument.querySelectorAll(CONTRACT.artwork)].flatMap(svg => {
      const layers = [...svg.querySelectorAll('[id]')];
      const matched = keys.map(key => layers.filter(layer => layer.id === key));
      if (matched.some(matches => matches.length !== 1)) return [];
      const nodes = matched.map(matches => matches[0]);
      if (nodes.some(node => !node.matches(CONTRACT.photoLayer) && !node.querySelector(CONTRACT.photoLayer))) return [];
      // Nested photo-slot wrappers do not establish an independent sibling stack.
      if (nodes.some((node, i) => nodes.some((other, j) => i !== j && node.contains(other)))) return [];
      const positions = nodes.map(node => layers.indexOf(node));
      return [{ fields: fields.map((field, i) => ({ field, position: positions[i] }))
        .sort((a, b) => a.position - b.position).map(item => item.field) }];
    });
    // Ambiguous or missing bindings fall back to the app's original presentation.
    return candidates.length === 1 ? (root.dataset.teePhotoOrder === 'form' ? fields : candidates[0].fields) : null;
  }

  class PhotoStrip {
    constructor(root) {
      this.root = root;
      this.frame = 0;
      this.expanded = false;
      this.originalOrders = new Map();
      this.events = new AbortController();
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      this.observerOptions = { childList: true, subtree: true, attributes: true,
        attributeFilter: ['id', 'src', 'style', 'class', 'hidden', 'aria-hidden', 'aria-invalid', 'data-tee-photo-layout', 'data-tee-photo-copy', 'data-tee-photo-order-help', 'required', 'aria-required'] };
      this.observer = new (window.FizzyProductObserver?.Observer || MutationObserver)(records => {
        if (records.some(record => !record.target.closest?.('.tee-photo-tray'))) this.schedule();
      });
      // The app's SVG renderer can live outside the product section.
      this.artworkObserver = new MutationObserver(records => {
        if (this.root.dataset.teePhotoLayout !== 'strip') return;
        const affectsArtwork = records.some(record =>
          record.target.closest?.(CONTRACT.artwork) ||
          [...record.addedNodes, ...record.removedNodes].some(node =>
            node.nodeType === 1 &&
            (node.matches(CONTRACT.artwork) || node.querySelector(CONTRACT.artwork))
          )
        );
        if (affectsArtwork) this.schedule();
      });
      this.syncArtworkObserver();
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(root);
      window.addEventListener('resize', this.schedule, { signal: this.events.signal });
      this.observer.observe(root, this.observerOptions);
      root.addEventListener('click', event => {
        if (this.root.dataset.teePhotoLayout === 'strip' && event.target.closest('#teeAtcButton')) {
          // Reveal required slots before the app performs its own validation.
          this.expanded = true;
          this.update();
          // Native validation runs during this click. Recompact on the next frame;
          // retain only fields carrying app-owned validation or upload messages.
          this.expanded = false;
          this.schedule();
        }
      }, { capture: true, signal: this.events.signal });
      root.addEventListener('change', event => {
        if (event.target.closest('.tee-field--template')) this.expanded = false;
        this.schedule();
      }, { signal: this.events.signal });
      this.update();
    }

    syncArtworkObserver() {
      const active = this.root.dataset.teePhotoLayout === 'strip';
      if (active === this.artworkObserved) return;
      this.artworkObserved = active;
      if (active) this.artworkObserver.observe(this.root.ownerDocument.body, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['id', 'class', 'hidden', 'style']
      });
      else this.artworkObserver.disconnect();
    }

    update() {
      if (!this.root.isConnected) return;
      this.syncArtworkObserver();
      // Disconnect while changing our presentation; observe app changes afterwards.
      this.observer.disconnect();
      try {
        this.applyLayout();
      } finally {
        this.observer.observe(this.root, this.observerOptions);
      }
    }

    applyLayout() {
      if (this.root.dataset.teePhotoLayout === 'two-columns') {
        this.applyColumns();
        return;
      }
      this.resetColumns();
      if (this.root.dataset.teePhotoLayout !== 'strip') {
        this.reset();
        return;
      }
      for (const [node] of this.originalOrders) {
        if (!this.root.contains(node)) this.originalOrders.delete(node);
      }
      const fields = [...this.root.querySelectorAll(fieldSelector)];
      const visible = fields.filter(field =>
        isFieldShown(field) &&
        field.querySelector('.tee-photo-label') &&
        field.querySelector('.tee-btn--upload') &&
        field.querySelector('input[type="file"]')
      );
      const eligible = resolveArtworkOrder(this.root, visible);
      if (!eligible) {
        this.reset();
        return;
      }
      // Offer empty slots from bottom to top in the app's actual paint stack.
      this.root.classList.toggle('tee-photo-strip-active', eligible.length > 0);
      let offeredEmpty = false;
      [...eligible, ...fields.filter(field => !eligible.includes(field))].forEach(field => {
        const uploaded = Boolean(field.querySelector('.tee-photo-uploaded .tee-photo-image'));
        const invalid = Boolean(field.querySelector(CONTRACT.fieldError)?.textContent.trim());
        const hasMessage = invalid || Boolean(field.querySelector(CONTRACT.uploadMessage)?.textContent.trim());
        field.classList.toggle('tee-photo-invalid', eligible.includes(field) && invalid);
        // Native messages keep their field ownership; only presentation expands.
        field.classList.toggle('tee-photo-message-active', eligible.includes(field) && hasMessage);
        field.classList.toggle('tee-photo-tile', eligible.includes(field));
        field.classList.toggle('tee-photo-filled', uploaded);
        const offerable = eligible.includes(field) && !uploaded && !this.expanded;
        field.classList.toggle('tee-photo-pending', offerable && offeredEmpty && !hasMessage);
        if (offerable) offeredEmpty = true;
      });
      this.updateHeading(eligible);
      this.updateOrder(eligible);
      this.updateTray(eligible.filter(field => !field.classList.contains('tee-photo-pending')));
    }

    // Read the app's own field presentation; do not store a second requirement setting.
    getRequiredState(field) {
      const heading = field.querySelector('.tee-field__header .tee-field__heading');
      if (!heading) return null;
      return Boolean(
        heading.querySelector('.tee-asterisk') ||
        field.querySelector('input[type="file"][required], input[type="file"][aria-required="true"]')
      );
    }

    updateHeading(fields) {
      const count = fields.length;
      const inputs = this.root.querySelector('#teeFormInputs');
      if (!inputs || !count) {
        this.removeOrderHelp();
        this.heading?.remove();
        this.heading = null;
        return;
      }
      if (!this.heading || this.heading.parentElement !== inputs) {
        this.heading?.remove();
        this.heading = document.createElement('div');
        this.heading.className = 'tee-field__heading tee-photo-strip-heading';
        inputs.append(this.heading);
      }
      const states = fields.map(field => this.getRequiredState(field));
      const known = states.every(state => state !== null);
      let copy;
      try { copy = JSON.parse(this.root.dataset.teePhotoCopy || '{}'); } catch (_) { return; }
      const category = new Intl.PluralRules(document.documentElement.lang || undefined).select(count);
      const mode = !known ? 'neutral' : states.every(Boolean) ? 'required' : 'optional';
      const forms = copy[mode] || copy.neutral || {};
      const text = (forms[category] || forms.other || '').replaceAll('[count]', new Intl.NumberFormat(document.documentElement.lang || undefined).format(count));
      let label = this.heading.querySelector('.tee-photo-strip-heading__label');
      if (!label) {
        label = document.createElement('span');
        label.className = 'tee-photo-strip-heading__label';
        this.heading.replaceChildren(label);
      }
      if (label.textContent !== text) label.textContent = text;
      this.updateOrderHelp(count, copy.orderHelp);
    }

    updateOrderHelp(count, copy) {
      // Explicit pilot selection is required: SVG paint order alone does not prove
      // that a motif overlaps (grids and face-cutout layouts also have paint order).
      const enabled = count > 1 && this.root.dataset.teePhotoOrderHelp === 'overlap' &&
        this.root.dataset.teePhotoOrder !== 'form' &&
        typeof this.heading.showPopover === 'function' &&
        ['title', 'body', 'replace', 'sequence', 'close'].every(key => typeof copy?.[key] === 'string' && copy[key].trim());
      if (!enabled) {
        this.removeOrderHelp();
        return;
      }
      const signature = JSON.stringify(copy);
      if (this.orderHelp?.parentElement === this.heading && this.orderHelp.dataset.copy === signature) return;
      this.removeOrderHelp();
      const wrap = document.createElement('span');
      wrap.className = 'fizzy-photo-order';
      wrap.dataset.copy = signature;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'fizzy-photo-order__trigger';
      button.textContent = 'ⓘ';
      button.setAttribute('aria-label', copy.title);
      button.setAttribute('aria-haspopup', 'dialog');
      const panel = document.createElement('div');
      panel.id = 'FizzyPhotoOrder-' + (++helpSequence);
      panel.className = 'fizzy-photo-order__panel';
      panel.setAttribute('popover', 'auto');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-labelledby', panel.id + '-title');
      button.setAttribute('popovertarget', panel.id);
      const title = document.createElement('h2');
      title.id = panel.id + '-title';
      title.textContent = copy.title;
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'fizzy-photo-order__close';
      close.textContent = '×';
      close.setAttribute('aria-label', copy.close);
      close.setAttribute('popovertarget', panel.id);
      close.setAttribute('popovertargetaction', 'hide');
      close.setAttribute('autofocus', '');
      panel.append(title, close);
      for (const key of ['body', 'replace', 'sequence']) {
        const paragraph = document.createElement('p');
        paragraph.textContent = copy[key];
        if (key === 'sequence') paragraph.className = 'fizzy-photo-order__sequence';
        panel.append(paragraph);
      }
      wrap.append(button, panel);
      this.heading.append(wrap);
      this.orderHelp = wrap;
    }

    removeOrderHelp() {
      const panel = this.orderHelp?.querySelector('[popover]');
      if (panel?.matches(':popover-open')) panel.hidePopover();
      this.orderHelp?.remove();
      this.orderHelp = null;
    }

    updateOrder(fields) {
      const inputs = this.root.querySelector('#teeFormInputs');
      if (!inputs) return;
      const nativeChildren = [...inputs.children].filter(node =>
        node !== this.heading && node !== this.tray
      );
      const notes = nativeChildren.filter(node =>
        node.matches('[data-tee-photo-note], .tee-photo-upload-note')
      );
      const group = [...notes, ...fields];
      const anchor = nativeChildren.findIndex(node => group.includes(node));
      for (const node of nativeChildren) {
        if (!this.originalOrders.has(node)) {
          this.originalOrders.set(node, {
            value: node.style.getPropertyValue('order'),
            priority: node.style.getPropertyPriority('order')
          });
        }
      }
      if (anchor < 0) {
        this.restoreOrder();
        return;
      }
      // Group slots at their native anchor; use verified SVG order within the group.
      const stride = group.length + 2;
      nativeChildren.forEach((node, index) => {
        const slot = group.indexOf(node);
        const value = String(slot >= 0 ? anchor * stride + slot + 1 : index * stride);
        if (node.style.getPropertyValue('order') !== value) node.style.setProperty('order', value);
      });
      const headingOrder = String(anchor * stride);
      if (this.heading && this.heading.style.order !== headingOrder) this.heading.style.order = headingOrder;
    }

    restoreOrder() {
      this.originalOrders.forEach((original, node) => {
        if (original.value) node.style.setProperty('order', original.value, original.priority);
        else node.style.removeProperty('order');
      });
      this.originalOrders.clear();
    }

    updateTray(fields) {
      const inputs = this.root.querySelector('#teeFormInputs');
      if (!inputs || !fields.length) {
        this.tray?.remove();
        this.tray = null;
        return;
      }
      if (!this.tray || this.tray.parentElement !== inputs) {
        this.tray?.remove();
        this.tray = document.createElement('div');
        this.tray.className = 'tee-photo-tray';
        this.tray.setAttribute('aria-hidden', 'true');
        inputs.append(this.tray);
      }
      // A decorative backdrop only: app-owned fields never change parents.
      const origin = inputs.getBoundingClientRect();
      const boxes = fields.map(field => field.getBoundingClientRect());
      const left = Math.max(0, Math.min(...boxes.map(box => box.left)) - origin.left - 6);
      const top = Math.max(0, Math.min(...boxes.map(box => box.top)) - origin.top - 6);
      const right = Math.min(origin.width, Math.max(...boxes.map(box => box.right)) - origin.left + 6);
      const bottom = Math.max(...boxes.map(box => box.bottom)) - origin.top + 6;
      this.tray.style.left = `${left + inputs.scrollLeft}px`;
      this.tray.style.top = `${top + inputs.scrollTop}px`;
      this.tray.style.width = `${right - left}px`;
      this.tray.style.height = `${bottom - top}px`;
    }

    resetColumns() {
      this.root.style.removeProperty('--tee-photo-columns');
      if (this.root.classList.contains('tee-photo-columns-active')) this.root.classList.remove('tee-photo-columns-active');
      this.root.querySelectorAll('.tee-photo-column-item').forEach(node => {
        node.classList.remove('tee-photo-column-item');
        node.style.removeProperty('--tee-photo-grid-row');
        node.style.removeProperty('--tee-photo-grid-column');
      });
    }

    applyColumns() {
      // Existing column placement stays in place while measuring, for the same reason as isFieldShown.
      this.resetStrip();
      const inputs = this.root.querySelector('#teeFormInputs');
      if (!inputs) {
        this.resetColumns();
        return;
      }
      const children = [...inputs.children].filter(node =>
        getComputedStyle(node).display !== 'none'
      );
      const groups = [];
      for (let i = 0; i < children.length;) {
        // Keep adjacent native filter/photo pairs together, without moving nodes.
        const pairs = [];
        let end = i;
        while (children[end]?.matches('.tee-field--additional_option') &&
          children[end].querySelector('.tee-clipart-row') &&
          children[end + 1]?.matches('.tee-field--photo') &&
          children[end + 1].querySelector(CONTRACT.fileInput)) {
          pairs.push([children[end], children[end + 1]]);
          end += 2;
        }
        if (pairs.length > 1) {
          groups.push({ pairs });
          i = end;
        } else {
          groups.push({ node: children[i++] });
        }
      }
      const longestRun = Math.max(0, ...groups.map(group => group.pairs?.length || 0));
      const tokens = getComputedStyle(this.root);
      const minimum = parseFloat(tokens.getPropertyValue('--tee-photo-pair-min-width'));
      const gap = parseFloat(tokens.getPropertyValue('--tee-photo-pair-gap'));
      const maximum = parseInt(tokens.getPropertyValue('--tee-photo-pair-columns'), 10);
      const inputStyle = getComputedStyle(inputs);
      const inset = ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth']
        .reduce((sum, name) => sum + (parseFloat(inputStyle[name]) || 0), 0);
      const width = inputs.getBoundingClientRect().width - inset;
      if (!longestRun || !Number.isFinite(minimum) || minimum <= 0 ||
        !Number.isFinite(gap) || gap < 0 || !Number.isFinite(maximum) || maximum < 1 || width <= 0) {
        this.resetColumns();
        return;
      }
      let columns = Math.min(longestRun, maximum);
      // Keep four pairs balanced: four across, or two by two in a narrow form.
      while (columns > 1 && columns * minimum + (columns - 1) * gap > width) {
        columns = Math.ceil(columns / 2);
      }
      const setStyle = (node, name, value) => {
        if (node.style.getPropertyValue(name) !== value) node.style.setProperty(name, value);
      };
      setStyle(this.root, '--tee-photo-columns', String(columns));
      let row = 1;
      const placed = new Set();
      const place = (node, r, column) => {
        placed.add(node);
        if (!node.classList.contains('tee-photo-column-item')) node.classList.add('tee-photo-column-item');
        setStyle(node, '--tee-photo-grid-row', String(r));
        setStyle(node, '--tee-photo-grid-column', column);
      };
      groups.forEach(group => {
        if (!group.pairs) {
          place(group.node, row++, '1 / -1');
          return;
        }
        group.pairs.forEach(([filter, photo], index) => {
          const line = Math.floor(index / columns);
          const lineLength = Math.min(columns, group.pairs.length - line * columns);
          // Half-width tracks center incomplete lines while preserving pair order.
          const start = columns - lineLength + (index % columns) * 2 + 1;
          const column = `${start} / span 2`;
          place(filter, row + line * 2, column);
          place(photo, row + line * 2 + 1, column);
        });
        row += Math.ceil(group.pairs.length / columns) * 2;
      });
      this.root.querySelectorAll('.tee-photo-column-item').forEach(node => {
        if (placed.has(node)) return;
        node.classList.remove('tee-photo-column-item');
        node.style.removeProperty('--tee-photo-grid-row');
        node.style.removeProperty('--tee-photo-grid-column');
      });
      if (!this.root.classList.contains('tee-photo-columns-active')) this.root.classList.add('tee-photo-columns-active');
    }

    reset() {
      this.resetColumns();
      this.resetStrip();
    }

    resetStrip() {
      this.removeOrderHelp();
      this.restoreOrder();
      this.tray?.remove();
      this.heading?.remove();
      this.tray = null;
      this.heading = null;
      this.expanded = false;
      // classList.remove writes the attribute even without a change, which wakes the other product observers.
      if (this.root.classList.contains('tee-photo-strip-active')) this.root.classList.remove('tee-photo-strip-active');
      const presentation = ['tee-photo-tile', 'tee-photo-filled', 'tee-photo-pending', 'tee-photo-invalid', 'tee-photo-message-active'];
      this.root.querySelectorAll(fieldSelector).forEach(field => {
        if (presentation.some(name => field.classList.contains(name))) field.classList.remove(...presentation);
      });
    }

    destroy() {
      this.observer.disconnect();
      this.artworkObserver.disconnect();
      this.resizeObserver.disconnect();
      this.events.abort();
      cancelAnimationFrame(this.frame);
      this.reset();
    }
  }

  function mount(scope = document) {
    for (const [root, instance] of roots) {
      if (!root.isConnected) { instance.destroy(); roots.delete(root); }
    }
    const matches = [...scope.querySelectorAll('[data-teeinblue-pdp]')];
    if (scope.matches?.('[data-teeinblue-pdp]')) matches.push(scope);
    matches.forEach(root => {
      if (!roots.has(root)) roots.set(root, new PhotoStrip(root));
    });
  }
  const options = { signal: lifecycle.signal };
  document.addEventListener('shopify:section:load', event => mount(event.target), options);
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, instance] of roots) {
      if (event.target === root || event.target.contains(root)) {
        instance.destroy(); roots.delete(root);
      }
    }
  }, options);
  document.addEventListener('teeinblue-event-component-injected', () => mount(), options);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(), { ...options, once: true });
  } else mount();
  window.TeePhotoStrip = {
    destroy() {
      lifecycle.abort();
      roots.forEach(instance => instance.destroy());
      roots.clear();
      delete window.TeePhotoStrip;
    }
  };
})();
